import * as React from 'react';

import type Moment from 'moment';
import moment from 'moment';

import {Checkbox} from 'shells/checkbox/checkbox';
import {FormField} from 'shells/form/form-field';
import {CalendarSquareRegularEventIcon} from 'shells/icon/svg/calendar-square-regular-event-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {SelectTimePicker} from 'shells/select-time-picker/select-time-picker';
import {Body} from 'shells/typography/body';

import {FormDatePicker} from '../../../entity-list/filters/forms/form-date-picker';

import styles from './calendar.module.css';

type RangeContainerProps = {
    first: React.ReactNode;
    second: React.ReactNode;
};

const RangeContainer = (props: RangeContainerProps) => {
    return (
        <div className='flex gap-4 align-center'>
            {props.first}
            <Body>-</Body>
            {props.second}
        </div>
    );
};

type Props = {
    startTime: number;
    endTime: number;
    isAllDay: boolean;
    isMultiDay: boolean;
    onStartDateChange: (date: number) => void;
    onEndDateChange: (date: number) => void;
    // @ts-expect-error
    onStartTimeChange: (startTime: Moment) => void;
    // @ts-expect-error
    onEndTimeChange: (endTime: Moment) => void;
    onAllDayChange: (isAllDay: boolean) => void;
    isReadOnly?: boolean;
};

export function DateTimeSection(props: Props) {
    const {isReadOnly = false} = props;

    return (
        <IconSection icon={CalendarSquareRegularEventIcon}>
            <div className='flex flex-dir-col gap-8'>
                <div className='flex gap-8'>
                    {props.isAllDay ? (
                        <RangeContainer
                            first={
                                <FormField innerId='startDate' noMargin={true} isFullWidth={true}>
                                    <FormDatePicker
                                        // @ts-expect-error
                                        name='startDate'
                                        value={props.startTime}
                                        dateFormat='ddd, MMM D, YYYY'
                                        onChange={props.onStartDateChange}
                                        isDisabled={isReadOnly}
                                        isLightMode={true}
                                        shouldDisableFocusTrapZone={false}
                                    />
                                </FormField>
                            }
                            second={
                                <FormField innerId='endDate' noMargin={true} isFullWidth={true}>
                                    <FormDatePicker
                                        // @ts-expect-error
                                        name='endDate'
                                        value={props.endTime}
                                        dateFormat='ddd, MMM D, YYYY'
                                        onChange={props.onEndDateChange}
                                        isDisabled={isReadOnly}
                                        isLightMode={true}
                                        shouldDisableFocusTrapZone={false}
                                    />
                                </FormField>
                            }
                        />
                    ) : (
                        <>
                            <FormField innerId='startDate' noMargin={true}>
                                <div className={styles['date-picker-field']}>
                                    <FormDatePicker
                                        // @ts-expect-error
                                        name='startDate'
                                        value={props.startTime}
                                        dateFormat='ddd, MMM D, YYYY'
                                        onChange={props.onStartDateChange}
                                        isDisabled={isReadOnly}
                                        isLightMode={true}
                                        isFullWidth={true}
                                        shouldDisableFocusTrapZone={false}
                                    />
                                </div>
                            </FormField>
                            <RangeContainer
                                first={
                                    <div className={styles['time-picker-container']}>
                                        <FormField
                                            innerId='startTime'
                                            noMargin={true}
                                            isFullWidth={true}
                                        >
                                            <SelectTimePicker
                                                maxTime='23:44' // Allows the end time to always have a valid time within 15 minute intervals
                                                selectedTime={moment.unix(props.startTime)}
                                                onChange={props.onStartTimeChange}
                                                allowPastTimes={true}
                                                minHeight={34}
                                                additionalTimes={[moment.unix(props.startTime)]}
                                                isDisabled={isReadOnly}
                                                isCreatable={true}
                                                createOnBlur={true}
                                                dateForCreation={moment.unix(props.startTime)}
                                            />
                                        </FormField>
                                    </div>
                                }
                                second={
                                    <div className={styles['time-picker-container']}>
                                        <FormField
                                            innerId='endTime'
                                            noMargin={true}
                                            isFullWidth={true}
                                        >
                                            <SelectTimePicker
                                                selectedTime={moment.unix(props.endTime)}
                                                onChange={props.onEndTimeChange}
                                                allowPastTimes={true}
                                                minHeight={34}
                                                additionalTimes={[moment.unix(props.endTime)]}
                                                baselineTime={moment.unix(props.startTime)}
                                                isDisabled={isReadOnly}
                                                isCreatable={true}
                                                createOnBlur={true}
                                                dateForCreation={moment.unix(props.endTime)}
                                            />
                                        </FormField>
                                    </div>
                                }
                            />
                            {props.isMultiDay && (
                                <div className={styles['date-picker-field']}>
                                    <FormField innerId='endDate' noMargin={true}>
                                        <FormDatePicker
                                            // @ts-expect-error
                                            name='endDate'
                                            value={props.endTime}
                                            dateFormat='ddd, MMM D, YYYY'
                                            isDisabled={isReadOnly}
                                            onChange={props.onEndDateChange}
                                            isLightMode={true}
                                            isFullWidth={true}
                                            shouldDisableFocusTrapZone={false}
                                        />
                                    </FormField>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {!isReadOnly && (
                    <FormField innerId='allDay' noMargin={true} isFullWidth={true}>
                        <Checkbox
                            label='All day'
                            checked={props.isAllDay}
                            onChange={props.onAllDayChange}
                            disabled={isReadOnly}
                        />
                    </FormField>
                )}
            </div>
        </IconSection>
    );
}
